/**
 * SAR money formatting for the P14 product-revenue cascade.
 *
 * Backend serialises Decimal as a string ("1850000.00" or null),
 * mirroring Pydantic v2's default. These helpers turn that into a
 * SAR-localised display string consistent across the cascade view +
 * any future surface that consumes annual_revenue_at_risk /
 * daily_revenue_at_risk.
 *
 * Why en-SA: en-SA renders SAR as the prefix "SAR " (English) + the
 * Western Arabic numerals + 0dp by default. Switching to ar-SA later
 * for the Arabic UI lane is a one-line locale change + the symbol
 * naturally swaps to ر.س. The currency string "SAR" is the SAMA
 * standard ISO 4217 code; do NOT use the Unicode currency glyph at
 * v0.14.0 (some operator terminals strip the bidi marks + render the
 * glyph in a confusing position).
 *
 * `formatSAR(null)` returns the "not measured" sentinel
 * (`MONEY_NOT_MEASURED`) so the UI layer can choose its own copy
 * via i18n rather than baking English in here.
 */

export const MONEY_NOT_MEASURED = "__not_measured__";

const SAR_FORMATTER = new Intl.NumberFormat("en-SA", {
  style: "currency",
  currency: "SAR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format a Decimal-as-string SAR figure for display.
 *
 * @param raw - The backend serialisation: "1850000.00", "0", "0.00",
 *   or `null` (== "not measured" semantically). An empty string is
 *   treated as null; any non-numeric input returns null sentinel.
 * @returns SAR-localised display string ("SAR 1,850,000") or
 *   `MONEY_NOT_MEASURED` for the null branch.
 */
export function formatSAR(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") {
    return MONEY_NOT_MEASURED;
  }
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) {
    return MONEY_NOT_MEASURED;
  }
  return SAR_FORMATTER.format(n);
}

const SAR_COMPACT_FORMATTER = new Intl.NumberFormat("en-SA", {
  style: "currency",
  currency: "SAR",
  notation: "compact",
  // Without a minimum, compact notation PADS the lower tiers: 950,000
  // reads "SAR 950.00K" and zero reads "SAR 0.00". The point of the
  // compact form is that a figure can be taken in at a glance, and two
  // trailing zeroes on a round number work against that.
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Compact SAR for tight surfaces -- "SAR 14.97B", "SAR 41.02M",
 * "SAR 950K". Same null-sentinel semantics as `formatSAR`.
 *
 * Takes a number as well as the backend's Decimal-as-string, so a
 * caller holding a parsed figure does not have to stringify it back.
 * That existed as the excuse for two hand-rolled `formatShort` copies
 * in the constellation with NO BILLIONS TIER -- which is how an estate
 * carrying SAR 14.97bn of revenue at risk reported "SAR 14971.4m/yr"
 * on a screen a bank's executive committee reads.
 */
export function formatSARCompact(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === "") {
    return MONEY_NOT_MEASURED;
  }
  const n = typeof raw === "number" ? raw : Number.parseFloat(raw);
  if (!Number.isFinite(n)) {
    return MONEY_NOT_MEASURED;
  }
  return SAR_COMPACT_FORMATTER.format(n);
}

/**
 * True when the formatted value is the "not measured" sentinel.
 * Lets the caller branch on display copy via i18n.
 */
export function isMoneyNotMeasured(formatted: string): boolean {
  return formatted === MONEY_NOT_MEASURED;
}
